'use client';

import { Grid, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { NewsCard } from '@/components/NewsCard';
import HexagonalButton from '@/components/bases/HexagonalButton';
import { useGA } from '@/hooks/useGA';

interface MediumPost {
  title: string;
  pubDate: string;
  link: string;
  description: string;
  thumbnail?: string;
}

export const MediumBlogSection = () => {
  const [posts, setPosts] = useState<MediumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { trackButtonClickEvent } = useGA();
  const router = useRouter();

  useEffect(() => {
    const fetchMediumPosts = async () => {
      try {
        const mediumRSS = 'https://medium.com/feed/@LFG_Inc';
        const url = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(mediumRSS)}&t=${Date.now()}`;
        console.log(url);

        const response = await fetch(url);
        const data = await response.json();

        if (data.status === 'ok' && data.items) {
          // Get the 3 newest posts
          const latestPosts = data.items.slice(0, 3).map((item: any) => {
            // Extract thumbnail - try description first, then content field
            let thumbnail = '';

            // Try description field first
            const descImgMatch = item.description?.match(/<img[^>]+src="([^">]+)"/);
            if (descImgMatch && descImgMatch[1]) {
              thumbnail = descImgMatch[1];
            }

            // If no thumbnail found in description, try content field
            if (!thumbnail && item.content) {
              const contentImgMatch = item.content.match(/<img[^>]+src="([^">]+)"/);
              if (contentImgMatch && contentImgMatch[1]) {
                thumbnail = contentImgMatch[1];
              }
            }

            // Extract plain text description (remove HTML tags)
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = item.description || '';
            const plainText = tempDiv.textContent || tempDiv.innerText || '';
            const excerpt = plainText.slice(0, 150) + (plainText.length > 150 ? '...' : '');

            return {
              title: item.title,
              pubDate: item.pubDate,
              link: item.link,
              description: excerpt,
              thumbnail,
            };
          });

          setPosts(latestPosts);
        } else {
          setError('Failed to fetch blog posts');
        }
      } catch (err) {
        console.error('Error fetching Medium posts:', err);
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchMediumPosts();
  }, []);

  if (loading) {
    return (
      <Stack
        width={'100%'}
        padding={{ xs: '40px 24px', sm: '80px' }}
        alignItems={'center'}
        justifyContent={'center'}
        sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
        <Typography color={'#FFFFFF'}>Loading latest news...</Typography>
      </Stack>
    );
  }

  if (error || posts.length === 0) {
    return null; // Gracefully hide section if there's an error
  }

  return (
    <Stack
      width={'100%'}
      padding={{ xs: '40px 24px', sm: '80px' }}
      alignItems={'center'}
      component={'section'}
      sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
      <Stack width={'min(1280px, 100%)'} gap={{ xs: '32px', sm: '40px' }}>
        {/* Header */}
        <Stack alignItems={'center'} width={'100%'}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            fontSize={{ xs: '24px', sm: '48px' }}
            lineHeight={{ xs: '32px', sm: '68px' }}
            textAlign={'center'}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            LATEST NEWS
          </Typography>
        </Stack>

        {/* News Cards */}
        <Grid container spacing={{ xs: 3, sm: 5 }}>
          {posts.map((post, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <NewsCard
                title={post.title}
                description={post.description}
                thumbnail={post.thumbnail}
                onClick={() => {
                  trackButtonClickEvent({ name: 'medium_post_click' });
                  // window.open(post.link, '_blank');
                  router.push(`/news/${index}`);
                }}
              />
            </Grid>
          ))}
        </Grid>

        {/* Read More Button */}
        <Stack alignItems={'center'} width={'100%'}>
          <Stack width={{ xs: '200px', sm: '247px' }}>
            <HexagonalButton
              label={'READ MORE'}
              variant={'secondary'}
              onClick={() => {
                trackButtonClickEvent({ name: 'medium_read_more' });
                router.push('/news');
                // window.open('https://medium.com/@LFG_Inc', '_blank');
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
